import {Component, Input, OnChanges, OnDestroy, OnInit, AfterViewInit, ViewChild} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Book} from "../../../../../model/Book";
import {BookService} from "../../../../service-data/book.service";
import {CoverService} from "../../../../service-data/cover.service";
import {BodyService} from "../../../../service-data/body.service";
import {ResourceService} from "../../../../service-data/resource.service";
import {bestAvailableStage, PdfArtifactStatus} from "../../../../service-ui/pdf-artifact-stage";
import {requestStagedImage, StageWatch} from "../../../../service-ui/staged-preview-image";

/**
 * Read-only cover viewer for a finalized Book.
 *
 * Uses `BookService.getImageURL(..., 'COVER', ...)` — the standardized cover
 * that will actually be sent to print. Display only; a Book is immutable.
 *
 * ── Why this component listens (added 2026-09-15) ────────────────────────
 * Same story as `BookPreviewSpreadComponent`: the cover only becomes final
 * after PREPARE (`Resource.prepared`) and STANDARDIZE (`Cover.standardized`),
 * and neither of those completing could previously reach this component.
 * `ngOnChanges` cannot fire for it, because `addUpdateLocalItem` mutates the
 * cached rows in place and the `[book]` @Input keeps its identity.
 *
 * ── The SADDLE_STITCH case ───────────────────────────────────────────────
 * A saddle-stitched book has no separate cover: its cover pages come out of
 * the **Body** PDF. So the status to watch is `Body.standardized`, not
 * `Cover.standardized`, and both services are subscribed rather than guessing
 * at construction time which one will matter.
 */
@Component({
  selector: 'app-book-preview-cover',
  templateUrl: './book-preview-cover.component.html',
  styleUrls: ['./book-preview-cover.component.css']
})
export class BookPreviewCoverComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('coverImage', { static: true }) coverImage;
  @Input() book: Book;
  @Input() dpi: number = 288;

  private readonly DISPLAY_DPI = 288;
  private readonly POINTS_PER_INCH = 72;
  private viewReady = false;

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();
  private stageWatch = new StageWatch();

  displayScale: number = 1.0;
  bleed: number = 15;

  /** Compute total cover width in pixels: (2 × trimWidth + spine) × displayScale + 2 × bleed */
  get coverDisplayWidth(): number {
    if (!this.book || !this.book.body) { return 800; }
    const trimWidth = this.book.body.width;
    const spine = this.book.body.spine || 0;
    return Math.round((trimWidth * 2 + spine) * this.displayScale) + 2 * this.bleed;
  }

  /** Compute total cover height in pixels: trimHeight × displayScale + 2 × bleed */
  get coverDisplayHeight(): number {
    if (!this.book || !this.book.body) { return 600; }
    const trimHeight = this.book.body.height;
    return Math.round(trimHeight * this.displayScale) + 2 * this.bleed;
  }

  constructor(private bookService: BookService,
              private coverService: CoverService,
              private bodyService: BodyService,
              private resourceService: ResourceService) {}

  /** Left edge of spine (end of back cover trim) */
  get spineLeftOffset(): number {
    if (!this.book || !this.book.body) { return 0; }
    return this.bleed + Math.round(this.book.body.width * this.displayScale);
  }

  /** Right edge of spine (start of front cover trim) */
  get spineRightOffset(): number {
    if (!this.book || !this.book.body) { return 0; }
    const spine = this.book.body.spine || 0;
    return this.bleed + Math.round((this.book.body.width + spine) * this.displayScale);
  }

  ngOnInit() {
    this.displayScale = this.DISPLAY_DPI / this.POINTS_PER_INCH; // 4.0

    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(resource => {
        if (resource && resource.getId && resource.getId() === this.coverResourceId()) {
          this.onStageMayHaveChanged();
        }
      });

    this.coverService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(cover => {
        if (cover && cover.getId && this.book && this.book.cover
          && cover.getId() === this.book.cover.ID) {
          this.onStageMayHaveChanged();
        }
      });

    // SADDLE_STITCH covers come out of the Body PDF, so Body.standardized is
    // the status that matters for them.
    this.bodyService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(body => {
        if (body && body.getId && this.book && this.book.body
          && body.getId() === this.book.body.ID) {
          this.onStageMayHaveChanged();
        }
      });
  }

  ngAfterViewInit() {
    this.viewReady = true;
    setTimeout(() => this.refreshImage(), 0);
  }

  ngOnChanges() {
    if (!this.viewReady) { return; }
    setTimeout(() => this.refreshImage(), 0);
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /** Whichever side actually supplies the cover artwork for this binding. */
  private coverSide(): any {
    if (!this.book) { return null; }
    return this.book.bindingType === 'SADDLE_STITCH' ? this.book.body : this.book.cover;
  }

  private coverResourceId(): number {
    const side = this.coverSide();
    return (side && side.resource_ID) || 0;
  }

  /**
   * `standardizes: true` because this is always a Book. Without the override
   * a `Cover`/`Body` row that has not loaded yet reads as "no standardize
   * stage" — i.e. as a Draft — and a PREPARED render is briefly declared
   * final, which is a flicker that says the wrong thing.
   */
  private artifactStatus(): PdfArtifactStatus {
    const side = this.coverSide();
    const resourceId = this.coverResourceId();
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return {
      resourceId: resourceId,
      prepared: resource ? resource.prepared : undefined,
      standardized: side ? side.standardized : undefined,
      standardizes: true
    };
  }

  private onStageMayHaveChanged(): void {
    if (!this.viewReady || !this.stageWatch.changed(this.artifactStatus())) {
      return;
    }
    this.refreshImage();
  }

  refreshImage() {
    if (!this.viewReady || !this.book || !this.book.ID) {
      return;
    }

    requestStagedImage({
      baseId: 'book-preview-' + this.book.ID + '-COVER-1-' + this.dpi,
      stage: bestAvailableStage(this.artifactStatus()),
      fetchUrl: () => this.bookService.getImageURL(this.book.ID, 'COVER', this.dpi, 1),
      onReady: cachedImage => this.applyCoverImage(cachedImage),
      onUnavailable: () => {
        this.resourceService.ensureProcessedResource(this.coverResourceId());
      }
    });
  }

  private applyCoverImage(cachedImage: any) {
    if (!this.coverImage || !this.coverImage.nativeElement) {
      return;
    }
    // `requestStagedImage` guarantees a real `src` and a completed load, so
    // the naturalWidth check that used to guard this is no longer load-bearing
    // — it is kept because a zero-dimension image still cannot be scaled.
    this.coverImage.nativeElement.src = cachedImage.src;
    this.coverImage.nativeElement.style.display = 'block';

    const naturalWidth = cachedImage.naturalWidth || cachedImage.width;
    const naturalHeight = cachedImage.naturalHeight || cachedImage.height;

    if (!naturalWidth || !naturalHeight) { return; }

    // Scale the image to fit within the cover display area
    this.coverImage.nativeElement.style.width = this.coverDisplayWidth + 'px';
    this.coverImage.nativeElement.style.height = this.coverDisplayHeight + 'px';
    this.coverImage.nativeElement.style.left = '0';
    this.coverImage.nativeElement.style.top = '0';
  }
}

